import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import type { WalletAnalysis } from '../services/api';
import { walletApi } from '../services/api';
import TransactionFlow from './TransactionFlow';

const WalletScanner = () => {
  const [walletAddress, setWalletAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [walletData, setWalletData] = useState<WalletAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [transactionPage, setTransactionPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionProgress, setTransactionProgress] = useState({ processed: 0, total: 0 });
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<string | null>(null);

  // Cleanup event source on unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  const handleScan = async () => {
    if (!walletAddress) {
      toast.error('Please enter a wallet address');
      return;
    }

    setLoading(true);
    try {
      const data = await walletApi.analyzeWallet(walletAddress, pageSize);
      setWalletData(data);
      // Reset transaction page when scanning new wallet
      setTransactionPage(1);
      setTransactions([]);
      setTransactionProgress({ processed: 0, total: 0 });
    } catch (error) {
      console.log('Failed to fetch wallet data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionHistory = async (page: number) => {
    if (!walletAddress) return;
    
    setLoadingTransactions(true);
    setTransactions([]);
    setTransactionProgress({ processed: 0, total: 0 });

    // Close existing event source if any
    if (eventSource) {
      eventSource.close();
    }

    // Create new event source using the API service
    const newEventSource = walletApi.getTransactionHistory(walletAddress, page, pageSize);
    setEventSource(newEventSource);

    newEventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch(data.type) {
        case 'count':
          setTransactionProgress(prev => ({ ...prev, total: data.total }));
          break;
          
        case 'transaction':
          setTransactions(prev => [...prev, data.transaction]);
          setTransactionProgress(data.progress);
          break;
          
        case 'error':
          console.error(`Error processing transaction ${data.signature}:`, data.error);
          toast.error(`Error processing transaction: ${data.error}`);
          break;
          
        case 'complete':
          setLoadingTransactions(false);
          newEventSource.close();
          setEventSource(null);
          break;
      }
    };

    newEventSource.onerror = (error) => {
      console.error('EventSource failed:', error);
      toast.error('Failed to fetch transaction history');
      setLoadingTransactions(false);
      newEventSource.close();
      setEventSource(null);
    };

    setTransactionPage(page);
  };

  const getCurrentBalance = (data: WalletAnalysis) => {
    if (data.investorDetails) {
      return data.investorDetails.currentBalance;
    }
    return data.walletDetails.currentBalance;
  };

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="flex flex-col gap-8">
        <h1 className="text-3xl font-bold text-center text-white">KoiiScan Tool</h1>
        
        <div className="w-full">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Enter wallet address"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="flex-1 px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center gap-2">
                <label htmlFor="initialPageSize" className="text-sm text-white whitespace-nowrap">Page Size:</label>
                <select
                  id="initialPageSize"
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value))}
                  className="px-3 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
              </div>
            </div>
            <button
              onClick={handleScan}
              disabled={loading}
              className="w-full px-6 py-3 text-lg font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Scanning...' : 'Scan Wallet'}
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Analyzing wallet...</p>
          </div>
        )}

        {walletData && (
          <div className="w-full">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'overview'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Overview
                </button>
                {walletData.staking && (
                  <button
                    onClick={() => setActiveTab('staking')}
                    className={`py-4 px-6 font-medium text-sm border-b-2 ${
                      activeTab === 'staking'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Staking
                  </button>
                )}
                <button
                  onClick={() => {
                    setActiveTab('transactions');
                    if (transactions.length === 0) {
                      loadTransactionHistory(1);
                    }
                  }}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'transactions'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Transactions
                </button>
              </nav>
            </div>

            <div className="mt-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="p-6 bg-white rounded-lg shadow">
                    <h2 className="text-xl font-semibold mb-4">Wallet Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {walletData.investorDetails ? (
                        <>
                          <div>
                            <p className="text-sm text-gray-500">Name</p>
                            <p className="text-lg font-medium">{walletData.investorDetails.name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Current Balance</p>
                            <p className="text-lg font-medium">
                              {getCurrentBalance(walletData).toFixed(2)} KOII
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Initial Balance</p>
                            <p className="text-lg font-medium">
                              {walletData.investorDetails.initialBalance.toFixed(2)} KOII
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="text-sm text-gray-500">Wallet Address</p>
                            <p className="text-lg font-medium break-all">{walletData.walletDetails.address}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Current Balance</p>
                            <p className="text-lg font-medium">
                              {getCurrentBalance(walletData).toFixed(2)} KOII
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="p-6 bg-white rounded-lg shadow">
                    <h2 className="text-xl font-semibold mb-4">Transfer Statistics</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-gray-500">Total Sent</p>
                        <p className="text-lg font-medium">{walletData.transfers.totalSent.toFixed(2)} KOII</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Received</p>
                        <p className="text-lg font-medium">{walletData.transfers.totalReceived.toFixed(2)} KOII</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Transfers</p>
                        <p className="text-lg font-medium">{walletData.transfers.totalTransfers}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'staking' && walletData.staking && (
                <div className="p-6 bg-white rounded-lg shadow">
                  <h2 className="text-xl font-semibold mb-4">Staking Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <p className="text-sm text-gray-500">Total Staking Balance</p>
                      <p className="text-lg font-medium">{walletData.staking.totalBalance.toFixed(2)} KOII</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Active Wallets</p>
                      <p className="text-lg font-medium">{walletData.staking.activeWallets}/{walletData.staking.totalWallets}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet Address</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {walletData.staking.wallets.map((wallet) => (
                          <tr key={wallet.walletAddress}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{wallet.walletAddress}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{wallet.balance.toFixed(2)} KOII</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'transactions' && (
                <div className="p-6 bg-white rounded-lg shadow">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Transaction History</h2>
                    <div className="flex items-center gap-2">
                      <label htmlFor="pageSize" className="text-sm text-gray-600">Page Size:</label>
                      <select
                        id="pageSize"
                        value={pageSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value);
                          setPageSize(newSize);
                          setTransactionPage(1);
                          loadTransactionHistory(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="10">10</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="200">200</option>
                      </select>
                    </div>
                  </div>

                  {loadingTransactions && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">
                          Loading transactions... {transactionProgress.processed} of {transactionProgress.total}
                        </span>
                        <span className="text-sm text-gray-600">
                          {Math.round((transactionProgress.processed / transactionProgress.total) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(transactionProgress.processed / transactionProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {transactions.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {transactions.map((tx) => (
                              <tr 
                                key={tx.signature} 
                                className="animate-fade-in cursor-pointer hover:bg-gray-50"
                                onClick={() => setSelectedTransaction(tx.signature)}
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.type}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                                  <a 
                                    href={`https://explorer.koii.live/tx/${tx.signature}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {tx.signature.slice(0, 8)}...{tx.signature.slice(-8)}
                                  </a>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.fromWallet}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.toWallet}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{tx.amount.toFixed(2)} KOII</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(tx.timestamp).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing {transactions.length} transactions (Page Size: {pageSize})
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadTransactionHistory(transactionPage - 1)}
                            disabled={transactionPage === 1 || loadingTransactions}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => loadTransactionHistory(transactionPage + 1)}
                            disabled={loadingTransactions}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-10 text-gray-500">
                      {loadingTransactions ? 'Loading transactions...' : 'No transactions found'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {selectedTransaction && (
          <TransactionFlow
            signature={selectedTransaction}
            onClose={() => setSelectedTransaction(null)}
          />
        )}
      </div>
    </div>
  );
};

export default WalletScanner; 